/** SUNTRONIX brand surfaces: hero/header band with icon pattern, and Quick Action cards with one oversized translucent icon. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { BUSINESS_WHATSAPP_LABEL, NEW_TAB, WHATSAPP_WEB_HELP, WHATSAPP_WEB_URL } from "@/lib/whatsappLinks";
import { BrandPattern } from "./BrandPattern";
import { SxIcon } from "./SxIcons";
import { QuickRepliesDialog } from "@/components/whatsapp/QuickRepliesDialog";

export function BrandHero({ set = "brand", density = "medium", children, className = "", testid = "brand-hero" }) {
  return (
    <section className={`relative overflow-hidden rounded-2xl border border-[color:var(--st-border)] bg-white ${className}`} data-testid={testid}>
      <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1.5 bg-[color:var(--st-brand)] sm:w-2" />
      <BrandPattern set={set} density={density} surface="dashboard" />
      <div className="relative pl-2 sm:pl-3">{children}</div>
    </section>
  );
}

export const HOME_ACTIONS = [
  { key: "quote", label: "New quote", hint: "Price & send in minutes", icon: "quote", to: "/commercial/crm/quotations?new=1" },
  { key: "quotations", label: "Quotations", hint: "Find · revise · resend", icon: "catalogue", to: "/commercial/crm/quotations" },
  { key: "gift-label", label: "Gift label", hint: "Walk-in · e-commerce · gift", icon: "box", to: "/admin/welcome-labels?tab=direct-sales" },
  { key: "product", label: "Add product", hint: "Catalogue workbench", icon: "laptop", to: "/catalogue-workbench" },
  { key: "warehouse", label: "Warehouse", hint: "Receive · store · custody", icon: "warehouse", to: "/operations/customer-owned-stock" },
  { key: "deploy", label: "Deploy", hint: "Ship to an employee", icon: "redeploy", to: "/deployment-centre" },
  { key: "recovery", label: "Recovery", hint: "Return · wipe · redeploy", icon: "recover", to: "/operations/asset-recovery" },
  { key: "service", label: "Service", hint: "Repairs & warranty", icon: "service", to: "/service" },
  { key: "whatsapp-web", label: BUSINESS_WHATSAPP_LABEL, hint: WHATSAPP_WEB_HELP, icon: "smartphone", href: WHATSAPP_WEB_URL },
  { key: "wa-quick-replies", label: "WhatsApp quick replies", hint: "Saved text to copy · nothing is sent", icon: "smartphone", dialog: true },
];

export function BrandQuickActions({ actions = HOME_ACTIONS, testid = "brand-quick-actions" }) {
  const navigate = useNavigate();
  const [qr, setQr] = useState(false);
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 xl:grid-cols-8" data-testid={testid}>
      {actions.some((a) => a.dialog) && <QuickRepliesDialog open={qr} onOpenChange={setQr} />}
      {actions.map((a, i) => {
        const cls = "stx-qa os-rise group rounded-xl border border-[color:var(--st-border)] bg-white/90 p-3 text-left backdrop-blur-sm";
        const body = (<>
          <SxIcon name={a.icon} className="stx-qa-icon" stroke={1.4} />
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)]"><SxIcon name={a.icon} size={18} /></span>
          <span className="mt-2 flex items-center gap-1 text-[13px] font-semibold text-[color:var(--st-text)]">{a.label}{a.href ? <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> : <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />}</span>
          <span className="block text-[11px] text-[color:var(--st-text-muted)]">{a.hint}</span>
          {a.href && <span className="sr-only"> (opens in a new tab)</span>}
        </>);
        return a.href
          ? <a key={a.key} href={a.href} {...NEW_TAB} className={cls} style={{ animationDelay: `${i * 40}ms` }} data-testid={`brand-qa-${a.key}`}>{body}</a>
          : <button key={a.key} type="button" onClick={() => (a.dialog ? setQr(true) : navigate(a.to))} className={cls} style={{ animationDelay: `${i * 40}ms` }} data-testid={`brand-qa-${a.key}`}>{body}</button>;
      })}
    </div>
  );
}
